/**
 * Options Scene - Game settings and configuration
 */

import { Scene } from 'phaser';
import { EventBus } from '../../utils/EventBus';
import { ServiceLocator, Services } from '../../utils/ServiceLocator';
import { AudioManager } from '../../managers/AudioManager';
import { I18n } from '../../i18n/I18nService';
import type { Locale } from '../../i18n/I18nService';

export class OptionsScene extends Scene {
  private activeTweens: Phaser.Tweens.Tween[] = [];

  constructor() {
    super('OptionsScene');
  }

  create(): void {
    const { width, height } = this.scale;

    // Background
    this.createBackground(width, height);

    // Title
    this.add
      .text(width / 2, 80, I18n.t('options.title'), {
        fontSize: '48px',
        color: '#aaccff',
        fontStyle: 'bold',
        fontFamily: 'Arial',
        letterSpacing: 4,
      })
      .setOrigin(0.5);

    // Settings sections
    this.createGraphicsSettings(width / 2 - 300, 200);
    this.createAudioSettings(width / 2 + 100, 200);
    this.createGameplaySettings(width / 2 - 300, 420);
    this.createLanguageSettings(width / 2 + 100, 420);

    // Back button
    this.createButton(width / 2, height - 100, I18n.t('menu.back_to_menu'), () => {
      this.scene.start('MainMenuScene');
    });

    EventBus.emit('current-scene-ready', this);
  }

  private createBackground(width: number, height: number): void {
    // Dark gradient background
    const bg = this.add.graphics();
    bg.fillGradientStyle(0x0a0a1a, 0x0a0a1a, 0x1a1a2a, 0x1a1a2a, 1);
    bg.fillRect(0, 0, width, height);
  }

  private createGraphicsSettings(x: number, y: number): void {
    const panel = this.createPanel(x, y, 400, 180, I18n.t('options.graphics'));

    // Resolution setting
    this.createSettingRow(panel, x + 20, y + 60, I18n.t('options.resolution'), '1920x1080');

    // Quality setting
    this.createSettingRow(panel, x + 20, y + 100, I18n.t('options.quality'), 'High');

    // Fullscreen toggle
    this.createToggle(panel, x + 20, y + 140, I18n.t('options.fullscreen'), true);
  }

  private createAudioSettings(x: number, y: number): void {
    const panel = this.createPanel(x, y, 400, 180, I18n.t('options.audio'));
    const audioManager = ServiceLocator.get<AudioManager>(Services.AUDIO);

    // Master volume
    this.createAudioSlider(
      panel, x + 20, y + 60, I18n.t('options.master_volume'),
      audioManager.getMasterVolume(),
      (v) => audioManager.setMasterVolume(v),
    );

    // Music volume
    this.createAudioSlider(
      panel, x + 20, y + 110, I18n.t('options.music_volume'),
      audioManager.getMusicVolume(),
      (v) => audioManager.setMusicVolume(v),
    );

    // SFX volume (non-functional placeholder)
    this.createSlider(panel, x + 20, y + 160, I18n.t('options.sfx_volume'), 90);
  }

  private createGameplaySettings(x: number, y: number): void {
    const panel = this.createPanel(x, y, 400, 180, I18n.t('options.gameplay'));

    // Difficulty
    this.createSettingRow(panel, x + 20, y + 60, I18n.t('options.difficulty'), 'Normal');

    // Auto-save
    this.createToggle(panel, x + 20, y + 100, I18n.t('options.autosave'), true);

    // Show tooltips
    this.createToggle(panel, x + 20, y + 140, I18n.t('options.show_tooltips'), true);
  }

  private createLanguageSettings(x: number, y: number): void {
    const panel = this.createPanel(x, y, 400, 180, I18n.t('options.language'));
    const locales = I18n.getAvailableLocales();
    const current = I18n.getLocale();

    locales.forEach((loc, i) => {
      const btnX = x + 20;
      const btnY = y + 55 + i * 28;
      const isActive = loc.code === current;

      const label = this.add.text(btnX + 30, btnY, loc.name, {
        fontSize: '16px',
        color: isActive ? '#ffffff' : '#aaccff',
        fontFamily: 'Arial',
        fontStyle: isActive ? 'bold' : 'normal',
      });

      const dot = this.add.circle(btnX + 10, btnY + 8, 6, isActive ? 0x00ffff : 0x2a2a3a);
      dot.setStrokeStyle(1, 0x4466aa);

      const hitArea = this.add.zone(btnX, btnY - 2, 360, 24).setOrigin(0, 0);
      hitArea.setInteractive({ useHandCursor: true });
      hitArea.on('pointerdown', () => {
        I18n.setLocale(loc.code as Locale);
        // Restart the scene to reflect new locale
        this.scene.restart();
      });

      panel.add([label, dot, hitArea]);
    });
  }

  private createPanel(
    x: number,
    y: number,
    width: number,
    height: number,
    title: string,
  ): Phaser.GameObjects.Container {
    const container = this.add.container(0, 0);

    // Panel background
    const bg = this.add.graphics();
    bg.fillStyle(0x0f0f1f, 0.9);
    bg.fillRoundedRect(x, y, width, height, 8);
    bg.lineStyle(2, 0x4466aa, 0.8);
    bg.strokeRoundedRect(x, y, width, height, 8);

    // Title
    const titleText = this.add
      .text(x + width / 2, y + 25, title, {
        fontSize: '24px',
        color: '#88aadd',
        fontFamily: 'Arial',
        letterSpacing: 2,
      })
      .setOrigin(0.5);

    container.add([bg, titleText]);
    return container;
  }

  private createSettingRow(
    container: Phaser.GameObjects.Container,
    x: number,
    y: number,
    label: string,
    value: string,
  ): void {
    const labelText = this.add.text(x, y, label, {
      fontSize: '18px',
      color: '#aaccff',
      fontFamily: 'Arial',
    });

    const valueText = this.add.text(x + 200, y, value, {
      fontSize: '18px',
      color: '#ffffff',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    });

    // Left arrow
    const leftArrow = this.add
      .text(x + 160, y, '◀', {
        fontSize: '18px',
        color: '#6688bb',
      })
      .setInteractive()
      .on('pointerdown', () => {
        console.log(`[Options] Decrease ${label}`);
      });

    // Right arrow
    const rightArrow = this.add
      .text(x + 320, y, '▶', {
        fontSize: '18px',
        color: '#6688bb',
      })
      .setInteractive()
      .on('pointerdown', () => {
        console.log(`[Options] Increase ${label}`);
      });

    container.add([labelText, valueText, leftArrow, rightArrow]);
  }

  private createAudioSlider(
    container: Phaser.GameObjects.Container,
    x: number,
    y: number,
    label: string,
    initialValue: number,
    onChange: (value: number) => void,
  ): void {
    const trackX = x + 200;
    const trackWidth = 150;
    const trackHeight = 8;

    const labelText = this.add.text(x, y, label, {
      fontSize: '18px',
      color: '#aaccff',
      fontFamily: 'Arial',
    });

    // Slider track
    const track = this.add.graphics();
    track.fillStyle(0x2a2a3a, 1);
    track.fillRoundedRect(trackX, y + 5, trackWidth, trackHeight, 4);

    // Slider fill
    const fill = this.add.graphics();
    const drawFill = (v: number) => {
      fill.clear();
      fill.fillStyle(0x4466aa, 1);
      fill.fillRoundedRect(trackX, y + 5, trackWidth * v, trackHeight, 4);
    };
    drawFill(initialValue);

    // Value text
    const valueText = this.add.text(trackX + trackWidth + 10, y, `${Math.round(initialValue * 100)}%`, {
      fontSize: '18px',
      color: '#ffffff',
      fontFamily: 'Arial',
    });

    // Clickable hit area over the track
    const hitZone = this.add.zone(trackX + trackWidth / 2, y + 5 + trackHeight / 2, trackWidth, 24)
      .setInteractive({ useHandCursor: true });

    hitZone.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      const ratio = Phaser.Math.Clamp((pointer.x - trackX) / trackWidth, 0, 1);
      onChange(ratio);
      drawFill(ratio);
      valueText.setText(`${Math.round(ratio * 100)}%`);
    });

    container.add([labelText, track, fill, valueText, hitZone]);
  }

  private createSlider(
    container: Phaser.GameObjects.Container,
    x: number,
    y: number,
    label: string,
    value: number,
  ): void {
    const labelText = this.add.text(x, y, label, {
      fontSize: '18px',
      color: '#aaccff',
      fontFamily: 'Arial',
    });

    // Slider track
    const track = this.add.graphics();
    track.fillStyle(0x2a2a3a, 1);
    track.fillRoundedRect(x + 200, y + 5, 150, 8, 4);

    // Slider fill
    const fill = this.add.graphics();
    fill.fillStyle(0x4466aa, 1);
    fill.fillRoundedRect(x + 200, y + 5, (150 * value) / 100, 8, 4);

    // Value text
    const valueText = this.add.text(x + 360, y, `${value}%`, {
      fontSize: '18px',
      color: '#ffffff',
      fontFamily: 'Arial',
    });

    container.add([labelText, track, fill, valueText]);
  }

  private createToggle(
    container: Phaser.GameObjects.Container,
    x: number,
    y: number,
    label: string,
    enabled: boolean,
  ): void {
    const labelText = this.add.text(x, y, label, {
      fontSize: '18px',
      color: '#aaccff',
      fontFamily: 'Arial',
    });

    // Toggle background
    const toggleBg = this.add.graphics();
    toggleBg.fillStyle(enabled ? 0x446688 : 0x2a2a3a, 1);
    toggleBg.fillRoundedRect(x + 200, y, 60, 28, 14);

    // Toggle circle
    const toggleCircle = this.add.circle(x + (enabled ? 246 : 214), y + 14, 12, 0xffffff);

    // Make interactive
    const hitArea = new Phaser.Geom.Rectangle(x + 200, y, 60, 28);
    toggleBg.setInteractive(hitArea, Phaser.Geom.Rectangle.Contains);
    toggleBg.on('pointerdown', () => {
      console.log(`[Options] Toggle ${label}: ${!enabled}`);
    });

    container.add([labelText, toggleBg, toggleCircle]);
  }

  private createButton(x: number, y: number, text: string, onClick: () => void): void {
    const button = this.add.container(x, y);

    // Background
    const bg = this.add.graphics();
    bg.fillStyle(0x2a3a5a, 0.9);
    bg.fillRoundedRect(-120, -25, 240, 50, 8);
    bg.lineStyle(2, 0x6688bb, 0.8);
    bg.strokeRoundedRect(-120, -25, 240, 50, 8);

    // Text
    const buttonText = this.add
      .text(0, 0, text, {
        fontSize: '20px',
        color: '#aaccff',
        fontFamily: 'Arial',
        letterSpacing: 2,
      })
      .setOrigin(0.5);

    button.add([bg, buttonText]);

    // Make interactive
    button.setSize(240, 50);
    const hitArea = new Phaser.Geom.Rectangle(-120, -25, 240, 50);
    button.setInteractive(hitArea, Phaser.Geom.Rectangle.Contains);

    button.on('pointerover', () => {
      buttonText.setColor('#ffffff');
      this.activeTweens.push(
        this.tweens.add({
          targets: bg,
          alpha: 1,
          duration: 200,
        }),
      );
    });

    button.on('pointerout', () => {
      buttonText.setColor('#aaccff');
      this.activeTweens.push(
        this.tweens.add({
          targets: bg,
          alpha: 0.9,
          duration: 200,
        }),
      );
    });

    button.on('pointerdown', onClick);
  }

  shutdown(): void {
    this.activeTweens.forEach((tween) => tween.stop());
    this.activeTweens = [];
  }
}
